import { isPossibleNumber, parsePhoneNumberWithError } from 'libphonenumber-js';
import {
	coerce,
	define,
	mask,
	nullable,
	optional,
	string,
	trimmed,
} from 'superstruct';

import { emptyStringIsNull, userString } from './userString';

/**
 * Checks if number has correct digits and is in the format of +1234567890
 */
const dbPhoneNumber = (requiredErrorMessage: string) =>
	define<string>('phone number', (value, context) => {
		let validString: string;
		try {
			validString = mask(value, trimmed(emptyStringIsNull(string())));
		} catch {
			return { message: requiredErrorMessage, ...context };
		}

		if (isPossibleNumber(validString) && /^\+\d+$/gmu.test(validString)) {
			return true;
		} else {
			return { message: 'validations:Invalid PhoneNumber' };
		}
	});

/**
 * Ensure that a value is a phone number in E.123 international notation without grouping.
 */
export const phoneNumber = (requiredErrorMessage = 'validations:Required') =>
	coerce(
		dbPhoneNumber(requiredErrorMessage),
		userString(requiredErrorMessage),
		(value) => {
			try {
				return parsePhoneNumberWithError(value, 'US').number;
			} catch {
				return value;
			}
		}
	);

/**
 * Like phoneNumber() but the field is not required.
 * If a non-empty value is provided it is still validated and normalized to E.164.
 */
export const optionalPhoneNumber = () =>
	coerce(
		define<string | null>('phone number', (value) => {
			if (value === null || value === undefined) return true;
			const str = value as string;
			if (isPossibleNumber(str) && /\+\d+?/gmu.test(str)) {
				return true;
			}
			return { message: 'validations:Invalid PhoneNumber' };
		}),
		trimmed(optional(nullable(string()))),
		(value) => {
			if (!value) return null;
			try {
				return parsePhoneNumberWithError(value).number;
			} catch {
				return value;
			}
		}
	);

/**
 * Normalize phone numbers to E.164 format if possible, but also allow for other
 * "numbers" such as SMS/MMS short codes which cannot be represented in E.164
 * and are not globally unique.
 */
export const loosePhoneNumber = () =>
	coerce(
		trimmed(emptyStringIsNull(string())),
		string(),
		(value) => parsePhoneNumberWithError(value).number
	);
